import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import Sidebar from "./components/Sidebar";
import TestHeader from "./components/TestHeader";
import EvidenceViewer from "./components/EvidenceViewer";
import StatusDebugger from "./components/StatusDebugger";
import StatusUpdateButton from "./components/StatusUpdateButton";
import TagInfoButton from "./components/TagInfoButton";
import LoginModal from "./components/LoginModal";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

import { TestResult } from "./types";
import { SSTFApiClient, getStepJudgment } from "./services/api";
import { useTestResults } from "./hooks/useTestResults";
import { useBaselineUrls } from "./hooks/useBaselineUrls";
import { resolveRegressionThresholds } from "./utils/regressionEvidence";
import {
  mergeApiConfig,
  validateApiConfig,
  ApiConfig,
  saveApiConfigToStorage,
} from "./config/apiConfig";
import {
  convertApiTestResultToTestResult,
  ApiTestResult,
  SearchFilters,
  applyScenarioGroupFilter,
} from "./utils/dataTransform";
import { DebugPanel } from "./components/DebugPanel";
import { MANUAL_PATHS, buildManualUrl } from "./config/manualUrl";


interface SavedSearchInfo {
  workspaceName: string;
  projectName: string;
  workspaceId?: string;
  projectId?: string;
  testRunId: string;
  testRunName: string;
}

const AppContainer = styled.div`
  display: flex;
  height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  background-color: #f5f6fa;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderControls = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  gap: 12px;
`;

const CompletionOverlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: ${(props) => (props.isVisible ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  z-index: 2000;
  animation: ${(props) =>
    props.isVisible ? "fadeIn" : "fadeOut"} 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
`;

const CompletionPanel = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 48px 40px;
  border-radius: 20px;
  max-width: 550px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1);
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;
  animation: slideIn 0.4s ease-out;
  
  @keyframes slideIn {
    from {
      transform: translateY(-20px) scale(0.9);
      opacity: 0;
    }
    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const CompletionTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 28px;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const CompletionMessage = styled.p`
  margin: 0 0 32px 0;
  font-size: 18px;
  line-height: 1.6;
  opacity: 0.9;
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
`;

const CompletionStats = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  margin: 24px 0;
  backdrop-filter: blur(10px);
`;

const CompletionStat = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 8px 0;
  font-size: 16px;
  
  &:first-child {
    margin-top: 0;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CompletionStatLabel = styled.span`
  opacity: 0.8;
`;

const CompletionStatValue = styled.span`
  font-weight: 600;
  font-size: 18px;
`;

const CompletionButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const CompletionButton = styled.button<{ variant?: "primary" | "secondary" }>`
  padding: 14px 28px;
  border: none;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;
  position: relative;
  overflow: hidden;
  
  ${(props) =>
    props.variant === "secondary"
      ? `
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.3);
      transform: translateY(-2px);
    }
  `
      : `
    background: linear-gradient(45deg, #4caf50, #45a049);
    color: white;
    box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
    
    &:hover:not(:disabled) {
      background: linear-gradient(45deg, #45a049, #4caf50);
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SearchingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 20px 0;
  font-size: 16px;
  opacity: 0.9;
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AutoSearchTimer = styled.div`
  margin-top: 16px;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-size: 14px;
  opacity: 0.8;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background: ${(props) => (props.active ? "#007bff" : "white")};
  color: ${(props) => (props.active ? "white" : "#007bff")};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.active ? "#0056b3" : "#f8f9fa")};
  }
`;

const ConfigPanel = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 20px;
  left: 320px;
  width: 400px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 20px;
  z-index: 1000;
  display: ${(props) => (props.isVisible ? "block" : "none")};
  max-height: 80vh;
  overflow-y: auto;
`;

const ConfigTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #333;
`;

const ConfigSection = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const ConfigInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  margin: 4px 0 12px 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
`;

const ConfigSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  margin: 4px 0 12px 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
`;

const ConfigLabel = styled.label`
  display: block;
  font-size: 14px;
  color: #555;
  margin-bottom: 4px;
`;

const ConfigCheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  color: #555;
  margin: 8px 0 12px 0;
  cursor: pointer;
  line-height: 1.45;

  input {
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

const ConfigButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;

const ConfigButton = styled.button<{ variant?: "primary" | "secondary" }>`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: #007bff;
    color: white;
    
    &:hover:not(:disabled) {
      background: #0056b3;
    }
  `
      : `
    background: #6c757d;
    color: white;
    
    &:hover:not(:disabled) {
      background: #545b62;
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SearchInfo = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
`;

const SidebarFooterButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const SidebarFooterButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  border: 1px solid #007bff;
  border-radius: 6px;
  background: white;
  color: #007bff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: #f8f9fa;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  
  // Show login modal if not authenticated
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#f5f6fa',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e0e0e0',
          borderTopColor: '#007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p>Loading...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginModal onLogin={login} />;
  }

  return <AppMain />;
};

const AppMain: React.FC = () => {
  // URLパラメータを解析
  const urlParams = new URLSearchParams(window.location.search);
  const directWorkspaceId = urlParams.get("workspace_id");
  const directProjectId = urlParams.get("project_id");
  const directResultId = urlParams.get("result_id");
  const isDirectMode = !!(
    directWorkspaceId &&
    directProjectId &&
    directResultId
  );

  // API設定とUI状態管理
  const [showConfigPanel, setShowConfigPanel] = useState<boolean>(false);
  const [initialSearchExecuted, setInitialSearchExecuted] =
    useState<boolean>(false);
  const [searchExecutedAtLeastOnce, setSearchExecutedAtLeastOnce] =
    useState<boolean>(false);
  const [searchCompleted, setSearchCompleted] = useState<boolean>(false);
  const [searchStatus, setSearchStatus] = useState<
    "idle" | "preparing" | "searching" | "completed"
  >("idle");
  const [directModeLoading, setDirectModeLoading] =
    useState<boolean>(isDirectMode);
  const [directModeError, setDirectModeError] = useState<string | null>(null);
  // ダイレクトモードでのシナリオグループフィルタ条件
  const [directModeGroupFilter, setDirectModeGroupFilter] = useState<{
    testSuiteMetaId: string;
    executionEnvironmentName: string;
  } | null>(null);

  // 検索フォームの状態
  const [searchFilters, setSearchFilters] = useState<{
    workspace: string;
    project: string;
    testRunId: string;
    searchType: "analyzer_waiting" | "interrupted" | "all" | "needs_review";
    judgmentMismatchOnly: boolean;
  }>({
    workspace: "",
    project: "",
    testRunId: "",
    searchType: "analyzer_waiting",
    judgmentMismatchOnly: false,
  });

  // 判定データの状態管理
  const [humanJudgment, setHumanJudgment] = useState<string | null>(null);
  
  // 保存された検索条件の状態管理
  const [savedSearchInfo, setSavedSearchInfo] = useState<SavedSearchInfo>(
    () => {
      try {
        const saved = localStorage.getItem("testresult-search-filters");
        if (saved) {
          const data = JSON.parse(saved);
          return {
            workspaceName: data.workspaceName || "",
            projectName: data.projectName || "",
            testRunId: data.testRunId || "",
            testRunName: data.testRunName || "",
          };
        }
      } catch (error) {
        console.error("検索条件の読み込みに失敗:", error);
      }
      return {
        workspaceName: "",
        projectName: "",
        testRunId: "",
        testRunName: "",
      };
    }
  );

  // API設定
  const [apiConfig, setApiConfig] = useState<ApiConfig>(mergeApiConfig());
  const [apiClient, setApiClient] = useState<SSTFApiClient | null>(null);

  // API用の状態
  const {
    testResults: apiTestResults,
    workspaces,
    projects,
    testRuns,
    selectedResultFiles,
    operationLog,
    evidenceFiles,
    projectStatusIds,
    testStepStatusIds,
    template,
    stats,
    loading,
    loadingFiles,
    loadingOperationLog,
    loadingEvidenceFiles,
    loadingTemplate,
    updatingTestResult,
    error,
    searchTestResults,
    loadWorkspaces,
    loadProjects,
    loadTestRuns,
    loadProjectTemplate,
    loadFileMetadata,
    loadOperationLogAndEvidences,
    resetSelectedEvidence,
    markAsProcessed,
    clearError,
    updateTestResult,
    filteredResults,
    updateTestResultValue,
    fetchSingleResult,
    applyFilters,
    testTargets,
  } = useTestResults(apiClient);

  // 全プロジェクトのテストデータ（最初に全件読み込み）
  const [allProjectTestResults, setAllProjectTestResults] = useState<ApiTestResult[]>([]);
  const [allProjectTestResultsUI, setAllProjectTestResultsUI] = useState<TestResult[]>([]);  // 最新結果のみ（表示用）
  const [allProjectRawData, setAllProjectRawData] = useState<any[]>([]);  // TagInfoButton用の生データ（全件）
  const [allProjectTestResultsUIFull, setAllProjectTestResultsUIFull] = useState<TestResult[]>([]);  // 全件UI形式（ナビゲーション用）
  
  // ダイレクトモードでのシナリオグループフィルタリング適用
  const scenarioFilteredResults = useMemo(
    () => applyScenarioGroupFilter(filteredResults, directModeGroupFilter),
    [filteredResults, directModeGroupFilter]
  );

  // 現在のテスト結果（フィルタリングされたもの）
  // useMemo で安定化することで、selectedTest 経由で参照される useBaselineUrls などの
  // useEffect 依存配列が毎レンダーで再発火するのを防ぐ。
  const currentTestResults = useMemo(
    () =>
      scenarioFilteredResults.map((result) =>
        convertApiTestResultToTestResult(
          result,
          template || undefined,
          testStepStatusIds || undefined
        )
      ),
    [scenarioFilteredResults, template, testStepStatusIds]
  );

  // 選択されたテスト
  const [selectedTestId, setSelectedTestId] = useState<string>("");
  const [selectedStepIndex, setSelectedStepIndex] = useState<number>(0);
  const [completedTests, setCompletedTests] = useState<Set<string>>(new Set());
  const [showCompletion, setShowCompletion] = useState<boolean>(false);
  const [isSearchingNewCases, setIsSearchingNewCases] =
    useState<boolean>(false);
  const [autoSearchTimer, setAutoSearchTimer] = useState<NodeJS.Timeout | null>(
    null
  );
  const [nextSearchCountdown, setNextSearchCountdown] = useState<number>(0);
  const [userClosedDialog, setUserClosedDialog] = useState<boolean>(false);
  
  // ステップごとの判定状態管理
  const [stepJudgments, setStepJudgments] = useState<{ [testResultId: string]: { [stepOrder: number]: string } }>({});
  
  // ステップごとのAI解釈正誤状態管理
  const [stepInterpretations, setStepInterpretations] = useState<{ [testResultId: string]: { [stepOrder: number]: boolean } }>({});

  // 選択されたテストを現在の表示または全データから取得
  const selectedTest = currentTestResults.find(
    (test: TestResult) => test.id === selectedTestId
  ) || allProjectTestResultsUIFull.find(
    (test: TestResult) => test.id === selectedTestId
  );

  // 選択 test_result の workspace/project (baseline URL 解決に必要)
  const selectedApiResultForBaseline = filteredResults.find(
    (r) => r.scriptTestRunResult.result_id === selectedTestId
  ) || allProjectTestResults.find(
    (r: any) => r.scriptTestRunResult?.result_id === selectedTestId
  );
  const baselineWorkspaceId = selectedApiResultForBaseline?.testCaseDetail?.workspace_id ?? "";
  const baselineProjectId = selectedApiResultForBaseline?.testCaseDetail?.project_id ?? "";
  const regressionBaseline = (selectedTest?.value as any)?.regression_baseline;
  const baselineUrls = useBaselineUrls(
    regressionBaseline,
    baselineWorkspaceId,
    baselineProjectId,
    apiClient
  );

  // MS-SSIM 閾値の解決（#1035 / follow-up）。
  // 解決順:
  //   1. test_result.value.regression_thresholds（判定時スナップショット）
  //   2. project.value（プロジェクト現在設定、判定後に変更されている可能性あり）
  //   3. ai_assist の ImageComparator デフォルト
  // 1 を優先することで、判定後にプロジェクト設定が変更されても当時の verdict が再現される。
  const regressionThresholds = useMemo(
    () =>
      resolveRegressionThresholds(
        selectedTest?.value,
        projects.find((p) => (p.project_id || p.id) === baselineProjectId)?.value,
      ),
    [selectedTest, projects, baselineProjectId],
  );
  
  // ステップ判定変更ハンドラー
  const handleStepJudgmentChange = (stepOrder: number, judgment: string) => {
    console.log(`handleStepJudgmentChange called: stepOrder=${stepOrder}, judgment=${judgment}`);
    console.log('selectedTestId:', selectedTestId);
    console.log('selectedTest:', selectedTest);
    
    if (!selectedTestId || !selectedTest) {
      console.log('早期リターン: selectedTestId または selectedTest が存在しません');
      return;
    }
    
    const updatedJudgments = {
      ...(stepJudgments[selectedTestId] || {})
    };
    
    // 現在のステップの判定を更新
    updatedJudgments[stepOrder] = judgment;
    console.log('更新後のjudgments:', updatedJudgments);
    
    // AI判定を取得（steps配列から）
    const step = selectedTest.steps[stepOrder - 1];
    const aiJudgmentStr = getStepJudgment(step?.statusId);

    // AIが誤った判定をした場合（人間判定とAI判定が異なる場合）
    if (judgment !== '未評価' && judgment !== aiJudgmentStr) {
      // それ以降のステップを自動的に「未評価」に設定
      for (let i = stepOrder + 1; i <= selectedTest.steps.length; i++) {
        if (!updatedJudgments[i]) {
          updatedJudgments[i] = '未評価';
        }
      }
    }
    
    setStepJudgments(prev => {
      const newState = {
        ...prev,
        [selectedTestId]: updatedJudgments
      };
      console.log('ステップ判定を更新しました:', newState);
      return newState;
    });
  };
  
  // ステップ解釈更新ハンドラー（ローカル状態のみ更新）
  const handleStepInterpretationUpdate = (stepOrder: number, correct: boolean) => {
    if (!selectedTestId) return;
    
    console.log(`ステップ ${stepOrder} のAI解釈を ${correct ? '正しい' : '誤り'} に設定`);
    
    setStepInterpretations(prev => ({
      ...prev,
      [selectedTestId]: {
        ...(prev[selectedTestId] || {}),
        [stepOrder]: correct
      }
    }));
  };
  const currentTestIndex = currentTestResults.findIndex(
    (test: TestResult) => test.id === selectedTestId
  );
  
  // テスト選択時に人間の判定をリセット
  useEffect(() => {
    if (selectedTest) {
      const existingJudgment = selectedTest.value?.tag_analysis?.human_judgment || null;
      setHumanJudgment(existingJudgment);
      
      // tag_analysis.stepsから各ステップの人間判定とAI解釈の正誤を読み込み
      const tagAnalysis = selectedTest.value?.tag_analysis;
      if (tagAnalysis?.steps && Array.isArray(tagAnalysis.steps)) {
        const initialJudgments: { [stepOrder: number]: string } = {};
        const initialInterpretations: { [stepOrder: number]: boolean } = {};
        
        tagAnalysis.steps.forEach((step: any) => {
          if (step.order) {
            if (step.human_judgment) {
              initialJudgments[step.order] = step.human_judgment;
            }
            // AI解釈の正誤が設定されている場合のみ読み込み
            if (step.ai_interpretation_correct !== undefined && step.ai_interpretation_correct !== null) {
              initialInterpretations[step.order] = step.ai_interpretation_correct;
            }
          }
        });
        
        // ステップごとの判定を初期化（既存の値があればマージ）
        setStepJudgments(prev => {
          const existingJudgments = prev[selectedTest.id] || {};
          // 既存の値と新しい値をマージ（新しい値を優先）
          const mergedJudgments = {
            ...existingJudgments,
            ...initialJudgments
          };
          return {
            ...prev,
            [selectedTest.id]: mergedJudgments
          };
        });
        
        // ステップごとのAI解釈の正誤を初期化
        if (Object.keys(initialInterpretations).length > 0) {
          setStepInterpretations(prev => {
            const existingInterpretations = prev[selectedTest.id] || {};
            // 既存の値と新しい値をマージ（新しい値を優先）
            const mergedInterpretations = {
              ...existingInterpretations,
              ...initialInterpretations
            };
            return {
              ...prev,
              [selectedTest.id]: mergedInterpretations
            };
          });
        }
      }
    }
  }, [selectedTestId]); // selectedTestIdのみを依存配列に含める

  const handleResultConfirm = async (result: "OK" | "NG") => {
    if (!selectedTest || !apiClient) return;

    // 選択されたAPIテスト結果を取得（現在の表示または全データから）
    const selectedApiResult = filteredResults.find(
      (apiResult) => apiResult.scriptTestRunResult.result_id === selectedTest.id
    ) || allProjectTestResults.find(
      (result: any) => result.scriptTestRunResult?.result_id === selectedTest.id
    );

    if (!selectedApiResult) {
      console.error("対応するAPIテスト結果が見つかりません");
      return;
    }

    try {
      // プロジェクト固有のステータスIDを使用
      let statusId: string;
      if (result === "OK") {
        statusId =
          projectStatusIds?.testResultStatus.OK ||
          "bf525e4e-24f6-56c2-a365-ded4a2a2beae"; // フォールバック値
      } else {
        statusId =
          projectStatusIds?.testResultStatus.NG ||
          "458cb677-0708-5af4-b926-bb7d398db6bb"; // フォールバック値
      }

      // テスト結果を更新
      await updateTestResult(
        selectedApiResult.testCaseDetail.workspace_id,
        selectedApiResult.testCaseDetail.project_id,
        selectedApiResult.testRunId,
        selectedApiResult.scriptTestRunResult.result_id,
        selectedApiResult.scriptTestRunResult.script_test_run_matrix_id,
        statusId,
        undefined, // actualTimeInSeconds
        selectedApiResult.scriptTestRunResult.value // currentValue
      );

      console.log(`テスト結果更新成功: ${selectedTest.name} -> ${result}`);

      // 現在のテストを完了済みとしてマーク
      setCompletedTests((prev) => new Set(prev).add(selectedTest.id));

      // 次のテストに移動
      const nextIndex = currentTestIndex + 1;
      if (nextIndex < currentTestResults.length) {
        setSelectedTestId(currentTestResults[nextIndex].id);
      } else {
        // 全てのテストが完了した場合
        setShowCompletion(true);
        // 自動的に新しいケースを検索
        await searchForNewCases();
      }
    } catch (error) {
      console.error("テスト結果更新エラー:", error);
      // エラーが発生した場合は次のテストに移動しない
    }
  };

  // 新しいケースを検索する関数
  const searchForNewCases = async () => {
    if (!apiClient || !searchFilters.workspace || !searchFilters.project) {
      return;
    }

    setIsSearchingNewCases(true);

    try {
      console.log("新しいテストケースを検索中...");

      // 検索実行フラグを設定
      setSearchExecutedAtLeastOnce(true);
      setSearchStatus("searching");

      // プロジェクトテンプレートを先に読み込み
      await loadProjectTemplate(searchFilters.workspace, searchFilters.project);

      // 現在のテスト結果数を記録
      const previousCount = currentTestResults.length;

      // 検索実行
      await searchTestResults(searchFilters);

      // 検索完了状態に設定
      setSearchStatus("completed");
      setSearchCompleted(true);

      // 検索完了の処理は useEffect で行う（previousCountを保存）
      (window as any).previousTestCount = previousCount;
    } catch (error) {
      console.error("新しいケース検索エラー:", error);
      setIsSearchingNewCases(false);
      // エラーの場合も自動検索タイマーを開始
      startAutoSearchTimer();
    }
  };

  // 自動検索タイマーを開始
  const startAutoSearchTimer = () => {
    // 既存のタイマーをクリア
    if (autoSearchTimer) {
      clearInterval(autoSearchTimer);
    }

    // 5分 = 300秒のカウントダウン
    setNextSearchCountdown(300);

    const timer: NodeJS.Timeout = setInterval(() => {
      setNextSearchCountdown((prev) => {
        if (prev <= 1) {
          // カウントダウン終了、検索実行
          searchForNewCases();
          return 300; // 次回のカウントダウンをリセット
        }
        return prev - 1;
      });
    }, 1000);

    setAutoSearchTimer(timer);
  };

  // 手動で再検索
  const handleManualSearch = () => {
    if (autoSearchTimer) {
      clearInterval(autoSearchTimer);
      setAutoSearchTimer(null);
    }
    setNextSearchCountdown(0);
    setUserClosedDialog(false); // 手動検索時はダイアログ表示を再有効化
    searchForNewCases();
  };

  const handleCompletionClose = () => {
    setShowCompletion(false);
    setUserClosedDialog(true); // ユーザーが明示的に閉じたことを記録
    // タイマーをクリア
    if (autoSearchTimer) {
      clearInterval(autoSearchTimer);
      setAutoSearchTimer(null);
    }
    setNextSearchCountdown(0);
    setIsSearchingNewCases(false);

    // ダイアログを閉じるだけ（完了状態や選択状態は維持）
  };

  // コンポーネントのアンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      if (autoSearchTimer) {
        clearInterval(autoSearchTimer);
      }
    };
  }, [autoSearchTimer]);

  // 時間をフォーマットする関数
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // 完了したテストの統計
  const completionStats = {
    total: currentTestResults.length,
    completed: completedTests.size,
    successCount: currentTestResults.filter(
      (test) => completedTests.has(test.id) && test.status === "success"
    ).length,
    failedCount: currentTestResults.filter(
      (test) =>
        completedTests.has(test.id) &&
        (test.status === "step_failed" ||
          test.status === "result_failed" ||
          test.status === "error")
    ).length,
  };

  // API設定の初期化
  useEffect(() => {
    console.log("API設定を初期化中...");
    const config = mergeApiConfig();
    console.log("読み込まれた設定:", config);
    console.log("設定前のapiConfig state:", apiConfig);
    setApiConfig(config);
    console.log("設定後のapiConfig state:", config);

    // 保存された設定があり、有効な場合はAPIクライアントを自動的に設定
    if (config.sstfBaseUrl) {
      const validation = validateApiConfig(config);
      if (validation.isValid) {
        const client = new SSTFApiClient(
          config.sstfBaseUrl,
          "", // userToken removed - using Auth0 authentication
          config.graphqlEndpoint
        );
        setApiClient(client);
        console.log("APIクライアントを自動的に設定しました");
      } else {
        console.log("設定の検証に失敗:", validation.errors);
      }
    }
  }, []);

  // apiConfigの変更を監視
  useEffect(() => {
    console.log("apiConfig stateが更新されました:", apiConfig);
  }, [apiConfig]);

  // 設定パネルが開かれたときにワークスペース一覧を取得
  useEffect(() => {
    if (showConfigPanel && apiClient && workspaces.length === 0) {
      loadWorkspaces();
    }
  }, [showConfigPanel, apiClient]);

  // ワークスペース一覧が読み込まれたら、保存された検索条件に基づいてプロジェクトを読み込む
  useEffect(() => {
    if (workspaces.length > 0 && apiClient && !initialSearchExecuted && !isDirectMode) {
      // 保存された検索条件を確認
      try {
        const saved = localStorage.getItem("testresult-search-filters");
        if (saved) {
          const data = JSON.parse(saved);
          
          // ワークスペースが保存されている場合
          if (data.workspace) {
            // ワークスペースをIDまたは名前で検索
            const selectedWorkspace = workspaces.find(
              (ws) => ws.workspace_id === data.workspace || 
                      ws.id === data.workspace ||
                      ws.name === data.workspaceName
            );
            
            if (selectedWorkspace) {
              const workspaceId = selectedWorkspace.workspace_id || selectedWorkspace.id;
              
              // 検索フィルターにワークスペースを設定
              setSearchFilters(prev => ({
                ...prev,
                workspace: workspaceId
              }));
              
              // プロジェクト一覧を読み込む
              console.log("保存されたワークスペースのプロジェクトを読み込み中:", selectedWorkspace.name);
              loadProjects(workspaceId);
            }
          }
        }
      } catch (error) {
        console.error("保存された検索条件の適用に失敗:", error);
      }
    }
  }, [workspaces, apiClient, initialSearchExecuted, isDirectMode, loadProjects]);

  // プロジェクト一覧が読み込まれたら、保存された検索条件を適用
  useEffect(() => {
    if (projects.length > 0 && apiClient && !initialSearchExecuted && !isDirectMode) {
      console.log("プロジェクト一覧が読み込まれました。保存された検索条件を確認中...");
      console.log("現在のsearchFilters.workspace:", searchFilters.workspace);
      console.log("projects:", projects);
      
      // 保存された検索条件を確認
      try {
        const saved = localStorage.getItem("testresult-search-filters");
        if (saved) {
          const data = JSON.parse(saved);
          console.log("保存されたデータ:", data);
          
          // プロジェクトが保存されている場合
          // searchFilters.workspaceがまだ設定されていない場合も、保存されたワークスペースがあれば処理を続行
          if (data.project && (searchFilters.workspace || data.workspace)) {
            // プロジェクトをIDまたは名前で検索
            const selectedProject = projects.find(
              (proj) => proj.project_id === data.project || 
                        proj.id === data.project ||
                        proj.name === data.projectName
            );
            
            console.log("選択されたプロジェクト:", selectedProject);
            
            if (selectedProject) {
              const projectId = selectedProject.project_id || selectedProject.id;
              const workspaceId = searchFilters.workspace || data.workspace;
              
              // 検索フィルターにプロジェクトを設定
              setSearchFilters(prev => ({
                ...prev,
                project: projectId
              }));
              
              // テストラン一覧を読み込む
              console.log("保存されたプロジェクトのテストランを読み込み中:", selectedProject.name);
              loadTestRuns(workspaceId, projectId);
            } else {
              console.log("保存されたプロジェクトが見つかりませんでした");
            }
          } else {
            console.log("プロジェクトまたはワークスペースが保存されていません");
          }
        }
      } catch (error) {
        console.error("保存されたプロジェクトの適用に失敗:", error);
      }
    }
  }, [projects, apiClient, initialSearchExecuted, isDirectMode, searchFilters.workspace, loadTestRuns]);

  // テストラン一覧が読み込まれたら、保存された検索条件を適用
  useEffect(() => {
    if (testRuns.length > 0 && apiClient && !initialSearchExecuted && !isDirectMode) {
      // 保存された検索条件を確認
      try {
        const saved = localStorage.getItem("testresult-search-filters");
        if (saved) {
          const data = JSON.parse(saved);
          
          // テストランが保存されている場合
          if (data.testRunId) {
            // テストランをIDで検索
            const selectedTestRun = testRuns.find(
              (tr) => tr.test_run_id === data.testRunId
            );
            
            if (selectedTestRun) {
              // 検索フィルターにテストランを設定
              setSearchFilters(prev => ({
                ...prev,
                testRunId: selectedTestRun.test_run_id
              }));
              
              console.log("保存されたテストランを適用:", selectedTestRun.name);
            }
          }
          
          // searchTypeも適用
          if (data.searchType) {
            setSearchFilters(prev => ({
              ...prev,
              searchType: data.searchType
            }));
          }

          if (data.judgmentMismatchOnly !== undefined) {
            setSearchFilters((prev) => ({
              ...prev,
              judgmentMismatchOnly: Boolean(data.judgmentMismatchOnly),
            }));
          }
        }
      } catch (error) {
        console.error("保存されたテストランの適用に失敗:", error);
      }
    }
  }, [testRuns, apiClient, initialSearchExecuted, isDirectMode]);

  // ESCキーで設定パネルを閉じる
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showConfigPanel) {
        setShowConfigPanel(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showConfigPanel]);

  // タグ詳細モーダルからのナビゲーションイベントリスナー
  useEffect(() => {
    const handleNavigateToTestResult = async (event: CustomEvent) => {
      const { resultId, stepIndex } = event.detail;
      console.log('ナビゲーションイベント受信:', { resultId, stepIndex });
      
      // まず現在の表示データから検索
      let targetTest = currentTestResults.find(test => test.id === resultId);
      console.log('現在の表示データからの検索結果:', targetTest);
      
      if (!targetTest) {
        // 全プロジェクトデータ（全件）から検索
        targetTest = allProjectTestResultsUIFull.find(test => test.id === resultId);
        console.log('全プロジェクトデータ（全件）からの検索結果:', targetTest);
      }
      
      if (targetTest) {
        console.log('選択を更新:', { resultId, stepIndex });
        
        // 選択を更新
        setSelectedTestId(resultId);
        setSelectedStepIndex(stepIndex);
        
        // スクロール位置を調整
        setTimeout(() => {
          const stepElement = document.querySelector(`[data-step-index="${stepIndex}"]`);
          console.log('スクロール対象要素:', stepElement);
          if (stepElement) {
            stepElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 200);
      } else {
        console.log('対象テストが見つかりません:', resultId);
        alert('選択されたテストケースが見つかりませんでした。');
      }
    };

    window.addEventListener('navigateToTestResult', handleNavigateToTestResult as unknown as EventListener);
    return () => {
      window.removeEventListener('navigateToTestResult', handleNavigateToTestResult as unknown as EventListener);
    };
  }, [currentTestResults, selectedTestId, allProjectTestResultsUIFull]);  // 全件データを使用

  // 全プロジェクトデータを読み込む関数
  // 全プロジェクトデータを読み込む関数（データ取得のみ）
  const loadAllProjectData = useCallback(async (workspaceId: string, projectId: string) => {
    if (!apiClient) return null;

    try {
      console.log('全プロジェクトデータを読み込み中...');

      let allRawData: any[] = [];
      let page = 1;
      const pageSize = 1000;
      let hasMore = true;

      // ページング処理で全件取得
      while (hasMore) {
        const findRequest = {
          page: page,
          size: pageSize,
          workspace_id: workspaceId,
          project_id: projectId,
          query: { and: [] } // フィルタなし
        };

        const response = await apiClient.findTestResults(findRequest);

        if (response?.script_test_result_details && response.script_test_result_details.length > 0) {
          allRawData = allRawData.concat(response.script_test_result_details);

          // 取得したデータがpageSizeより少ない場合は最後のページ
          if (response.script_test_result_details.length < pageSize) {
            hasMore = false;
          } else {
            page++;
            console.log(`ページ${page}を読み込み中...`);
          }
        } else {
          hasMore = false;
        }
      }

      console.log(`全${allRawData.length}件のデータを取得しました`);

      if (allRawData.length > 0) {
        // 生データを保存（TagInfoButton用） - 全件保存
        setAllProjectRawData(allRawData);

        // 全件をUI形式に変換（ナビゲーション用）
        const { transformApiTestResults } = await import('./utils/dataTransform');
        const allTransformed = transformApiTestResults(
          allRawData,
          {}, // workspaceSlugMap
          {}, // projectSlugMap
          apiConfig.sstfBaseUrl,
          template || undefined
        );

        const allUiResults = allTransformed.map((result: any) =>
          convertApiTestResultToTestResult(
            result,
            template || undefined,
            testStepStatusIds || undefined
          )
        );
        setAllProjectTestResultsUIFull(allUiResults);  // 全件保存
        console.log(`全テスト結果読み込み: ${allUiResults.length}件`);

        // API形式も保存
        setAllProjectTestResults(allTransformed);

        return allRawData;
      }
      return null;
    } catch (error) {
      console.error('全プロジェクトデータの読み込みに失敗:', error);
      return null;
    }
  }, [apiClient, apiConfig.sstfBaseUrl, template, testStepStatusIds]);

  // フィルタリング処理を適用する関数
  const applyProjectDataFilter = useCallback(async (filters: Partial<SearchFilters>, allRawData?: any[]) => {
    const dataToFilter = allRawData || allProjectRawData;

    if (!dataToFilter || dataToFilter.length === 0) {
      console.log('フィルタリングするデータがありません');
      return;
    }

    const { transformApiTestResults } = await import('./utils/dataTransform');

    // ステップ1: まず最新の結果のみをフィルタリング
    const latestResults = new Map();
    for (const result of dataToFilter) {
      // キーとなる組み合わせを作成
      const key = `${result.test_run.test_run_id}_${result.test_run_test_case.testcase_id}_${result.test_run_execution_environment.execution_environment_id}`;

      // 既存の結果がないか、より新しい場合のみ保存
      const existingResult = latestResults.get(key);
      if (!existingResult || result.script_test_run_result.modified_at > existingResult.script_test_run_result.modified_at) {
        latestResults.set(key, result);
      }
    }

    let filteredData = Array.from(latestResults.values());
    console.log(`最新の結果のみ: ${filteredData.length}件`);

    // ステップ2: searchTypeに応じた追加フィルタリング
    if (filters.searchType === 'analyzer_waiting') {
      // AI分析待ちのステータスでフィルタリング
      filteredData = filteredData.filter((result: any) => {
        const statusId = result.script_test_run_result?.test_result_status_id;
        // AIステータスID（例）- 実際のIDは確認が必要
        const aiStatusIds = [
          'cc2f6d67-67e8-5bc9-ad05-c58f91e8ec8b', // AI分析待ち
          '8a5ea4f2-c139-58ca-babe-9cbc0e693e29', // AI分析中
          '98f95e06-56c5-5f6f-bd8a-98dc37cbfe0f', // AI分析済み
          '28fcee5b-b5e5-5dc7-bc39-aa8c887e7e9d'  // エラー
        ];
        return aiStatusIds.includes(statusId);
      });
    } else if ((filters.searchType as any) === 'fail') {
      // NG結果のみフィルタリング
      filteredData = filteredData.filter((result: any) => {
        const statusId = result.script_test_run_result?.test_result_status_id;
        const ngStatusId = projectStatusIds?.testResultStatus.NG || '458cb677-0708-5af4-b926-bb7d398db6bb';
        return statusId === ngStatusId;
      });
    }
    // searchType === 'all' の場合は最新結果のみの状態を保持

    // ステップ3: テストランIDでのフィルタリング
    if (filters.testRunId) {
      filteredData = filteredData.filter((result: any) =>
        result.test_run?.test_run_id === filters.testRunId
      );
    }

    console.log(`フィルタ適用後: ${filteredData.length}件`);

    // UI形式に変換して保存
    const transformed = transformApiTestResults(
      filteredData,
      {}, // workspaceSlugMap
      {}, // projectSlugMap
      apiConfig.sstfBaseUrl,
      template || undefined
    );

    // フィルタリング結果を適用（useTestResultsフックのapplyFiltersを使用）
    await searchTestResults({
      workspace: filters.workspace || searchFilters.workspace,
      project: filters.project || searchFilters.project,
      testRunId: filters.testRunId || '',
      searchType: filters.searchType || 'all',
      judgmentMismatchOnly:
        filters.judgmentMismatchOnly ?? searchFilters.judgmentMismatchOnly,
    });

    console.log(`フィルタリング完了: ${filteredData.length}件`);
  }, [allProjectRawData, apiConfig.sstfBaseUrl, template, testStepStatusIds, projectStatusIds, searchTestResults, searchFilters]);
  
  // 初期化時の自動検索
  useEffect(() => {
    if (!apiClient || initialSearchExecuted || isDirectMode) {
      return;
    }

    // 保存された検索条件を確認
    try {
      const saved = localStorage.getItem("testresult-search-filters");
      if (saved) {
        const data = JSON.parse(saved);

        // ワークスペースとプロジェクトが両方保存されている場合のみ自動検索
        if (data.workspace && data.project) {
          console.log("保存された検索条件で自動検索を実行:", data);

          const filters = {
            workspace: data.workspace,
            project: data.project,
            testRunId: data.testRunId || "",
            searchType: data.searchType || "analyzer_waiting",
            judgmentMismatchOnly: data.judgmentMismatchOnly === true,
          };

          // 検索フォームの状態も更新
          setSearchFilters(filters);

          // 実行済みフラグを設定
          setInitialSearchExecuted(true);

          // 完了したテストをリセット
          setCompletedTests(new Set());

          // 検索準備状態に設定
          setSearchStatus("preparing");

          // 少し遅延を入れてAPIクライアントの初期化が完了してから実行
          setTimeout(async () => {
            try {
              console.log("自動検索を開始します");
              
              // まずworkspacesをロード
              if (!workspaces || workspaces.length === 0) {
                console.log('Workspacesをロード中...');
                await loadWorkspaces();
              }

              // 検索実行フラグを設定
              setSearchExecutedAtLeastOnce(true);
              setSearchStatus("searching");

              // プロジェクトテンプレートを先に読み込み
              if (data.workspace && data.project) {
                console.log(
                  "初期検索でプロジェクトテンプレートを読み込み中..."
                );
                await loadProjectTemplate(data.workspace, data.project);
              }

              console.log("テスト結果を検索中...");
              await searchTestResults(filters);
              
              // 全プロジェクトデータも読み込む
              if (data.workspaceId || data.workspace) {
                await loadAllProjectData(
                  data.workspaceId || data.workspace, 
                  data.projectId || data.project
                );
              }

              // 検索完了状態に設定
              setSearchStatus("completed");
              setSearchCompleted(true);

              // 検索実行後に保存された検索条件を更新
              console.log('検索実行時のworkspaces:', workspaces);
              console.log('検索実行時のprojects:', projects);
              console.log('検索データ:', data);
              
              // workspacesとprojectsが空の場合はAPIから取得
              let workspaceList = workspaces;
              let projectList = projects;
              
              if (!workspaceList || workspaceList.length === 0) {
                console.log('ワークスペースリストが空なので取得中...');
                // loadWorkspaces関数を使用してステートを更新
                await loadWorkspaces();
                // APIから直接取得してローカル変数にも保存（後続の処理で使用）
                if (apiClient) {
                  workspaceList = await apiClient.getWorkspaces(true);
                  console.log('取得したworkspaces:', workspaceList);
                }
              }
              
              // workspaceのidで検索するか名前で検索するかを判定
              const workspaceIdOrName = data.workspace;
              let workspace = null;
              
              // まずIDで検索
              workspace = workspaceList.find(w => w.workspace_id === workspaceIdOrName || w.id === workspaceIdOrName);
              
              // IDで見つからなければ名前で検索
              if (!workspace) {
                workspace = workspaceList.find(w => w.name === (data.workspaceName || workspaceIdOrName));
              }
              
              console.log('見つかったworkspace:', workspace);
              
              if (workspace && (!projectList || projectList.length === 0)) {
                console.log('プロジェクトリストが空なので取得中...');
                const workspaceId = workspace.workspace_id || workspace.id;
                // loadProjects関数を使用してステートを更新
                await loadProjects(workspaceId);
                // APIから直接取得してローカル変数にも保存（後続の処理で使用）
                if (apiClient) {
                  projectList = await apiClient.getProjects(workspaceId, true);
                  console.log('取得したprojects:', projectList);
                }
              }
              
              // projectも同様にidと名前の両方で検索
              const projectIdOrName = data.project;
              let project = null;
              
              // まずIDで検索
              project = projectList.find(p => p.project_id === projectIdOrName || p.id === projectIdOrName);
              
              // IDで見つからなければ名前で検索
              if (!project) {
                project = projectList.find(p => p.name === (data.projectName || projectIdOrName));
              }
              
              console.log('見つかったproject:', project);
              
              // テストランを読み込む（workspace と project が両方ある場合）
              if (workspace && project) {
                const workspaceId = workspace.workspace_id || workspace.id;
                const projectId = project.project_id || project.id;
                
                console.log('テストラン一覧を読み込み中...');
                // loadTestRuns関数を使用してステートを更新
                await loadTestRuns(workspaceId, projectId);
              }
              
              const searchInfo = {
                workspaceName: workspace?.name || data.workspaceName || data.workspace || "",
                projectName: project?.name || data.projectName || data.project || "",
                workspaceId: workspace?.workspace_id || workspace?.id,
                projectId: project?.project_id || project?.id,
                testRunId: data.testRunId || "",
                testRunName: data.testRunName || "",
              };
              
              console.log('設定するsavedSearchInfo:', searchInfo);
              setSavedSearchInfo(searchInfo);

              console.log("自動検索が完了しました");
            } catch (error) {
              console.error("初期検索の実行に失敗:", error);
              setSearchStatus("idle");
            }
          }, 100);
        } else {
          console.log(
            "保存された検索条件が不完全なため、自動検索をスキップします"
          );
        }
      } else {
        console.log("保存された検索条件がないため、自動検索をスキップします");
      }
    } catch (error) {
      console.error("初期検索条件の読み込みに失敗:", error);
    }
  }, [apiClient, initialSearchExecuted, searchTestResults, loadWorkspaces, loadProjects, loadTestRuns]); // 依存関係を明示

  // ダイレクトモードの初期化処理
  useEffect(() => {
    if (!apiClient || !isDirectMode || initialSearchExecuted) {
      return;
    }

    const loadDirectTestResult = async () => {
      setDirectModeLoading(true);
      setSearchStatus("searching");
      setSearchExecutedAtLeastOnce(true);

      try {
        console.log("ダイレクトモードでテスト結果を読み込み中:", {
          workspaceId: directWorkspaceId,
          projectId: directProjectId,
          resultId: directResultId,
        });

        // プロジェクトテンプレートを先に読み込み
        await loadProjectTemplate(directWorkspaceId!, directProjectId!);

        // Phase 1: 指定result_idの1件を取得してメタデータを抽出
        const singleResult = await fetchSingleResult(
          directWorkspaceId!,
          directProjectId!,
          directResultId!
        );

        if (!singleResult) {
          throw new Error(`result_id: ${directResultId} のテスト結果が見つかりません`);
        }

        const testRunId = singleResult.test_run.test_run_id;
        const testSuiteMetaId = singleResult.test_run_test_suite.meta_id;
        const envName = singleResult.test_run_execution_environment.name;

        console.log("ダイレクトモード: シナリオグループ情報を取得しました", {
          testRunId,
          testSuiteMetaId,
          envName,
        });

        // フィルタ条件を保存
        setDirectModeGroupFilter({
          testSuiteMetaId,
          executionEnvironmentName: envName,
        });

        // Phase 2: 同一テストランの全テスト結果を取得
        // ダイレクトモードで指定された result_id は、同一 (test_run, testcase, env)
        // で再実行された古い result の場合に dedup で消えるため、必ず残すよう
        // keepResultIds で明示する（リグレッションテストの 1回目/2回目 履歴比較）。
        await searchTestResults({
          workspace: directWorkspaceId!,
          project: directProjectId!,
          testRunId: testRunId,
          searchType: "all",
          keepResultIds: [directResultId!],
        });

        // searchForNewCases用にsearchFiltersも設定
        setSearchFilters({
          workspace: directWorkspaceId!,
          project: directProjectId!,
          testRunId: testRunId,
          searchType: "all",
          judgmentMismatchOnly: false,
        });

        setInitialSearchExecuted(true);
        setSearchStatus("completed");
        setSearchCompleted(true);

        // ダイレクトモードでもsavedSearchInfoを設定
        setSavedSearchInfo({
          workspaceName: directWorkspaceId,
          projectName: directProjectId,
          workspaceId: directWorkspaceId,
          projectId: directProjectId,
          testRunId: testRunId,
          testRunName: singleResult.test_run.name,
        });

        // ダイレクトモードで設定パネルを隠す
        setShowConfigPanel(false);

        console.log("ダイレクトモードでのテスト結果読み込み完了");
      } catch (error) {
        console.error("ダイレクトモードでのテスト結果読み込みエラー:", error);
        setSearchStatus("idle");
        const errorMessage =
          error instanceof Error
            ? error.message
            : "テスト結果の読み込みに失敗しました";
        setDirectModeError(errorMessage);
      } finally {
        setDirectModeLoading(false);
      }
    };

    loadDirectTestResult();
  }, [
    apiClient,
    isDirectMode,
    initialSearchExecuted,
    directWorkspaceId,
    directProjectId,
    directResultId,
    loadProjectTemplate,
    searchTestResults,
    fetchSingleResult,
  ]);

  // 初期テスト選択と検索結果の処理
  useEffect(() => {
    if (currentTestResults.length > 0 && !selectedTestId) {
      if (isDirectMode && directResultId) {
        const directResult = currentTestResults.find((t) => t.id === directResultId);
        if (directResult) {
          setSelectedTestId(directResult.id);
          return;
        }
      }
      setSelectedTestId(currentTestResults[0].id);
    }
  }, [currentTestResults, selectedTestId, isDirectMode, directResultId]);

  // 検索状態に応じたダイアログ表示処理
  useEffect(() => {
    // 検索準備中の場合（初期化時）
    if (searchStatus === "preparing" && !showCompletion) {
      console.log("検索準備中のダイアログを表示");
      setShowCompletion(true);
    }

    // エラーが発生した場合（ユーザーが明示的に閉じていない場合、かつ設定パネルが開いていない場合のみ）
    if (error && !userClosedDialog && !showCompletion && !showConfigPanel) {
      console.log("エラーが発生したため、ダイアログを表示");
      setShowCompletion(true);
    }

    // 検索が完了し、結果が0件の場合（ユーザーが明示的に閉じていない場合のみ）
    if (
      searchStatus === "completed" &&
      !loading &&
      currentTestResults.length === 0 &&
      !isSearchingNewCases &&
      !userClosedDialog &&
      !error
    ) {
      console.log("検索結果が0件でした");
      if (!showCompletion) {
        setShowCompletion(true);
      }
    }

    // 検索が完了し、結果がある場合はダイアログを閉じる（エラーがない場合のみ）
    if (
      searchStatus === "completed" &&
      !loading &&
      currentTestResults.length > 0 &&
      showCompletion &&
      !isSearchingNewCases &&
      !error
    ) {
      console.log("検索結果が見つかったため、ダイアログを閉じる");
      setShowCompletion(false);
    }
  }, [
    searchStatus,
    loading,
    currentTestResults.length,
    showCompletion,
    isSearchingNewCases,
    userClosedDialog,
    error,
    showConfigPanel,
  ]);

  // 検索結果の変更を監視して、新しいテストケースが追加された場合の処理
  useEffect(() => {
    if (!isSearchingNewCases || !showCompletion) {
      return;
    }

    const previousCount = (window as any).previousTestCount;
    if (typeof previousCount === "number") {
      const newCount = currentTestResults.length;
      const hasNewTests = newCount > previousCount;

      if (hasNewTests) {
        // 新しいテストがある場合は完了ダイアログを閉じて継続
        console.log(
          `${newCount - previousCount}件の新しいテストケースが見つかりました`
        );

        // 自動検索タイマーをクリア
        if (autoSearchTimer) {
          clearInterval(autoSearchTimer);
          setAutoSearchTimer(null);
        }
        setNextSearchCountdown(0);

        // ダイアログを閉じて状態をリセット
        setShowCompletion(false);
        setCompletedTests(new Set());
        setIsSearchingNewCases(false);

        // 最初の新しいテストを選択
        if (currentTestResults.length > 0) {
          setSelectedTestId(currentTestResults[0].id);
        }

        // previousCountをクリア
        delete (window as any).previousTestCount;
      } else if (!loading) {
        // 新しいテストがなく、検索が完了している場合は5分後に再検索を設定
        console.log("新しいテストケースは見つかりませんでした");
        startAutoSearchTimer();
        setIsSearchingNewCases(false);
        // previousCountをクリア
        delete (window as any).previousTestCount;
      }
    }
  }, [
    currentTestResults,
    isSearchingNewCases,
    showCompletion,
    loading,
    autoSearchTimer,
  ]);

  // テスト結果を切り替えたら、まず前結果のエビデンスを即時クリアする (#433)。
  // この後のファイルメタ取得 → エビデンス取得は非同期で、かつ managed_file_list が
  // 空の結果ではロード自体が走らないため、クリアしないと前結果の動画が残ってしまう。
  // 必ずファイルメタ取得 effect より前に実行されるよう、ここに配置する。
  useEffect(() => {
    if (!selectedTestId) return;
    resetSelectedEvidence();
    // resetSelectedEvidence は安定参照（useCallback []）。selectedTestId 変化時のみ実行する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTestId]);

  // テスト結果選択時にファイルメタデータを取得
  useEffect(() => {
    if (!selectedTestId || !apiClient) {
      return;
    }

    // テスト結果が選択されるたびにファイルメタデータを取得

    const selectedApiResult = filteredResults.find(
      (result) => result.scriptTestRunResult.result_id === selectedTestId
    ) || allProjectTestResults.find(
      (result: any) => result.scriptTestRunResult?.result_id === selectedTestId
    );

    if (
      selectedApiResult &&
      selectedApiResult.scriptTestRunResult.managed_file_list &&
      selectedApiResult.scriptTestRunResult.managed_file_list.length > 0
    ) {
      // managed_file_listから file_ids を取得
      const fileIds =
        selectedApiResult.scriptTestRunResult.managed_file_list.map(
          (file) => file.managed_file_id
        );

      // ワークスペースIDとプロジェクトIDを取得
      const workspaceId = selectedApiResult.testCaseDetail.workspace_id;
      const projectId = selectedApiResult.testCaseDetail.project_id;

      console.log("ファイルメタデータを取得します:", {
        workspaceId,
        projectId,
        fileIds,
      });

      loadFileMetadata(workspaceId, projectId, fileIds);
    }
  }, [selectedTestId]);

  // ファイルメタデータ取得後に操作ログとエビデンスを取得
  useEffect(() => {
    console.log("操作ログ取得のuseEffect実行:", {
      selectedTestId,
      selectedResultFiles,
      apiClient,
    });

    if (!selectedTestId || !selectedResultFiles || !apiClient) {
      console.log("操作ログ取得の条件が満たされていません");
      return;
    }

    // 選択されたテスト結果からworkspaceIdとprojectIdを取得
    const selectedApiResult = filteredResults.find(
      (result) => result.scriptTestRunResult.result_id === selectedTestId
    ) || allProjectTestResults.find(
      (result: any) => result.scriptTestRunResult?.result_id === selectedTestId
    );

    if (selectedApiResult) {
      const workspaceId = selectedApiResult.testCaseDetail.workspace_id;
      const projectId = selectedApiResult.testCaseDetail.project_id;

      console.log("操作ログとエビデンスファイルを取得します", {
        workspaceId,
        projectId,
        filesCount: selectedResultFiles.length,
        testCaseId: selectedTestId,
      });

      loadOperationLogAndEvidences(
        workspaceId,
        projectId,
        selectedResultFiles,
        selectedTestId
      );

      // SSTF が発行する GCS V4 signed URL は X-Goog-Expires=600 (10 分)。
      // 同じ test_result を 10 分以上開いておくと img.src が 403 になり
      // current / diff 画像がリンク切れ表示になるので、proactive に再取得する。
      const refreshIntervalMs = 8 * 60 * 1000;
      const intervalId = window.setInterval(() => {
        loadOperationLogAndEvidences(
          workspaceId,
          projectId,
          selectedResultFiles,
          selectedTestId
        );
      }, refreshIntervalMs);
      return () => window.clearInterval(intervalId);
    }
  }, [selectedTestId, selectedResultFiles]);

  const handleApiConfigChange = (
    field: keyof ApiConfig,
    value: string | boolean
  ) => {
    setApiConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveConfig = () => {
    // 設定をlocalStorageに保存
    saveApiConfigToStorage(apiConfig);

    // 設定が有効な場合はAPIクライアントを設定
    const validation = validateApiConfig(apiConfig);
    if (validation.isValid) {
      const client = new SSTFApiClient(
        apiConfig.sstfBaseUrl,
        "", // userToken removed - using Auth0 authentication
        apiConfig.graphqlEndpoint
      );
      setApiClient(client);
      setShowConfigPanel(false);
      alert("設定を保存し、API接続を有効化しました。");
    } else {
      alert(
        `設定エラー:\n${validation.errors.join(
          "\n"
        )}\n\n設定を確認してください。`
      );
    }
  };

  const handleTestConnection = async () => {
    const validation = validateApiConfig(apiConfig);
    if (!validation.isValid) {
      alert(
        `設定エラー:\n${validation.errors.join(
          "\n"
        )}\n\n設定を確認してください。`
      );
      return;
    }

    try {
      const testClient = new SSTFApiClient(
        apiConfig.sstfBaseUrl,
        "", // userToken removed - using Auth0 authentication
        apiConfig.graphqlEndpoint
      );

      // まずプロキシサーバーの接続確認
      const proxyTest = await testClient.testProxyConnection();
      if (!proxyTest.success) {
        alert(
          `プロキシサーバー接続エラー:\n${proxyTest.message}\n\n以下を確認してください:\n1. docker-compose up -d でサービスが起動しているか\n2. プロキシサーバー（ポート3001）が起動しているか\n3. nginx設定が正しいか`
        );
        return;
      }

      console.log("プロキシサーバー接続成功、APIテスト開始");

      // APIの接続確認
      await testClient.getWorkspaces();
      alert(
        `接続テスト成功！\n\n✅ プロキシサーバー: ${proxyTest.message}\n✅ SSTF API: 正常に接続できました`
      );
    } catch (error) {
      console.error("接続テストエラー:", error);
      const errorMessage =
        error instanceof Error ? error.message : "不明なエラー";

      // エラーの種類に応じた詳細なメッセージ
      let detailedMessage = `接続テスト失敗:\n${errorMessage}\n\n`;

      if (errorMessage.includes("fetch")) {
        detailedMessage += "ネットワーク接続を確認してください:\n";
        detailedMessage +=
          "1. docker-compose up -d でサービスが起動しているか\n";
        detailedMessage +=
          "2. プロキシサーバー（proxy-server）が起動しているか\n";
        detailedMessage += "3. nginx設定でプロキシが正しく設定されているか";
      } else if (errorMessage.includes("401") || errorMessage.includes("403")) {
        detailedMessage += "認証エラーです:\n";
        detailedMessage += "1. ログイン状態を確認してください\n";
        detailedMessage += "2. セッションが有効か確認してください";
      } else if (errorMessage.includes("404")) {
        detailedMessage += "APIエンドポイントが見つかりません:\n";
        detailedMessage += "1. SSTF Base URLが正しいか確認してください\n";
        detailedMessage += "2. APIバージョンが正しいか確認してください";
      } else {
        detailedMessage += "設定を確認してください。";
      }

      alert(detailedMessage);
    }
  };

  // 検索フィルターの変更処理
  const handleSearchFilterChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value,
    }));

    // ワークスペースが変更された場合、プロジェクト一覧を読み込み
    if (field === "workspace" && value && apiClient) {
      loadProjects(value);
      // プロジェクトとテストランをリセット
      setSearchFilters((prev) => ({
        ...prev,
        project: "",
        testRunId: "",
      }));
    }

    // プロジェクトが変更された場合、テストラン一覧を読み込み
    if (field === "project" && value && apiClient) {
      const workspaceId = searchFilters.workspace;
      if (workspaceId) {
        loadTestRuns(workspaceId, value);
      }
      // テストランをリセット
      setSearchFilters((prev) => ({
        ...prev,
        testRunId: "",
      }));
    }
  };

  // 検索実行
  const handleSearch = async () => {
    if (!searchFilters.workspace || !searchFilters.project) {
      alert("ワークスペースとプロジェクトを選択してください。");
      return;
    }

    try {
      console.log("検索実行中...", searchFilters);

      // 手動検索時はダイレクトモード由来のシナリオグループ絞り込みを解除する。
      // 残したままだと新しい検索結果が古いダイレクトモードの test_suite/env で
      // 弾かれ「結果がありません」となる (tfact_user_request#191)。
      setDirectModeGroupFilter(null);

      // 検索条件をlocalStorageに保存
      console.log('検索時のworkspaces:', workspaces);
      console.log('検索時のprojects:', projects);
      console.log('検索フィルター:', searchFilters);
      
      // workspaceをIDまたは名前で検索
      const selectedWorkspace = workspaces.find(
        (ws) => ws.workspace_id === searchFilters.workspace || 
                ws.id === searchFilters.workspace ||
                ws.name === searchFilters.workspace
      );
      
      // projectをIDまたは名前で検索
      const selectedProject = projects.find(
        (proj) => proj.project_id === searchFilters.project || 
                  proj.id === searchFilters.project ||
                  proj.name === searchFilters.project
      );
      
      console.log('見つかったworkspace:', selectedWorkspace);
      console.log('見つかったproject:', selectedProject);

      const currentTestRun = testRuns.find(
        (tr) => tr.test_run_id === searchFilters.testRunId
      );

      const searchData = {
        workspace: searchFilters.workspace,
        project: searchFilters.project,
        testRunId: searchFilters.testRunId,
        searchType: searchFilters.searchType,
        judgmentMismatchOnly: searchFilters.judgmentMismatchOnly,
        workspaceName: selectedWorkspace?.name || "",
        projectName: selectedProject?.name || "",
        testRunName: currentTestRun?.name || "",
      };

      localStorage.setItem(
        "testresult-search-filters",
        JSON.stringify(searchData)
      );
      console.log("検索条件を保存しました:", searchData);

      // 完了したテストをリセット
      setCompletedTests(new Set());

      // ユーザーの閉じる操作をリセット（新しい検索開始時）
      setUserClosedDialog(false);

      // 検索実行フラグを設定
      setSearchExecutedAtLeastOnce(true);
      setSearchStatus("searching");

      // プロジェクトテンプレートを先に読み込み
      console.log("プロジェクトテンプレートを読み込み中...");
      await loadProjectTemplate(searchFilters.workspace, searchFilters.project);

      // 全プロジェクトデータを読み込む（キャッシュ用）
      await loadAllProjectData(searchFilters.workspace, searchFilters.project);

      // フィルタリングして表示用データを設定
      await searchTestResults(searchFilters);

      // 検索完了状態に設定
      setSearchStatus("completed");
      setSearchCompleted(true);

      // 検索実行後に保存された検索条件を更新
      const foundTestRun = testRuns.find(
        (tr) => tr.test_run_id === searchFilters.testRunId
      );
      setSavedSearchInfo({
        workspaceName: selectedWorkspace?.name || "",
        projectName: selectedProject?.name || "",
        workspaceId: selectedWorkspace?.workspace_id || selectedWorkspace?.id || searchFilters.workspace,
        projectId: selectedProject?.project_id || selectedProject?.id || searchFilters.project,
        testRunId: searchFilters.testRunId,
        testRunName: foundTestRun?.name || "",
      });

      setShowConfigPanel(false);
    } catch (error) {
      console.error("検索エラー:", error);
    }
  };

  // App.tsx の先頭付近に追加
  useEffect(() => {
    // ブラウザ拡張機能エラーの抑制
    const handleExtensionError = (event: ErrorEvent) => {
      if (event.message && event.message.includes("message channel closed")) {
        console.warn(
          "ブラウザ拡張機能のエラーを検出しました（アプリケーションには影響ありません）:",
          event.message
        );
        event.preventDefault();
        return true;
      }
    };

    window.addEventListener("error", handleExtensionError);

    return () => {
      window.removeEventListener("error", handleExtensionError);
    };
  }, []);

  // ダイレクトモード時のグループ情報（サイドバー表示用）
  const sidebarGroupInfo = useMemo(() => {
    if (!directModeGroupFilter || currentTestResults.length === 0) return null;
    const currentIndex = selectedTestId
      ? currentTestResults.findIndex((t) => t.id === selectedTestId) + 1
      : 1;
    const matchedResult = scenarioFilteredResults[0];
    const testSuiteName = matchedResult?.testSuiteName || "";
    return {
      testSuiteName,
      currentIndex: Math.max(currentIndex, 1),
      totalCount: currentTestResults.length,
    };
  }, [directModeGroupFilter, currentTestResults, selectedTestId, scenarioFilteredResults]);

  return (
    <AppContainer>
      <Sidebar
        testResults={currentTestResults}
        selectedTestId={selectedTestId}
        onSelectTest={setSelectedTestId}
        completedTests={completedTests}
        apiTestResults={filteredResults}
        template={template}
        projectStatusIds={projectStatusIds?.testResultStatus}
        groupInfo={sidebarGroupInfo}
      >
        <SidebarFooterButtonContainer>
          <SidebarFooterButton
            onClick={() => {
              window.open(
                buildManualUrl(MANUAL_PATHS.resultCheckerMain),
                "_blank",
              );
            }}
          >
            📖 利用マニュアル
          </SidebarFooterButton>
          <SidebarFooterButton
            onClick={() => setShowConfigPanel(!showConfigPanel)}
          >
            ⚙️ 設定
          </SidebarFooterButton>
        </SidebarFooterButtonContainer>
      </Sidebar>
      <MainContent>
        {directModeError && (
          <div
            style={{
              padding: "20px",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              borderRadius: "4px",
              margin: "20px",
            }}
          >
            <h3>エラー</h3>
            <p>{directModeError}</p>
            <p>URL パラメータを確認してください:</p>
            <ul>
              <li>workspace_id: {directWorkspaceId || "未指定"}</li>
              <li>project_id: {directProjectId || "未指定"}</li>
              <li>result_id: {directResultId || "未指定"}</li>
            </ul>
          </div>
        )}
        {selectedTest && (
          <>
            <HeaderControls>
              <StatusUpdateButton
                workspaceId={(() => {
                  console.log('現在のsavedSearchInfo:', savedSearchInfo);
                  console.log('searchFilters:', searchFilters);
                  
                  // searchFiltersから直接取得を試みる（IDとして保存されている場合）
                  const workspaceIdFromFilters = searchFilters?.workspace;
                  const workspaceIdFromSaved = savedSearchInfo?.workspaceId;
                  const finalWorkspaceId = workspaceIdFromSaved || workspaceIdFromFilters || directWorkspaceId;
                  
                  console.log('workspaceId計算:', {
                    'searchFilters.workspace': workspaceIdFromFilters,
                    'savedSearchInfo?.workspaceId': workspaceIdFromSaved,
                    'directWorkspaceId': directWorkspaceId,
                    '最終的なworkspaceId': finalWorkspaceId
                  });
                  return finalWorkspaceId || undefined;
                })()}
                projectId={(() => {
                  const projectIdFromFilters = searchFilters?.project;
                  const projectIdFromSaved = savedSearchInfo?.projectId;
                  const finalProjectId = projectIdFromSaved || projectIdFromFilters || directProjectId;
                  
                  console.log('projectId計算:', {
                    'searchFilters.project': projectIdFromFilters,
                    'savedSearchInfo?.projectId': projectIdFromSaved,
                    'directProjectId': directProjectId,
                    '最終的なprojectId': finalProjectId
                  });
                  return finalProjectId || undefined;
                })()}
                onUpdate={async (wId, pId) => {
                  console.log('StatusUpdateButton onUpdate呼び出し:', { wId, pId });
                  if (!apiClient) {
                    throw new Error("APIクライアントが初期化されていません");
                  }
                  const result = await apiClient.updateReviewStatus(wId, pId);
                  // 必要に応じてテスト結果を再読み込み
                  if (result.updated > 0) {
                    // 検索を再実行して最新状態を反映
                    if (savedSearchInfo?.testRunId) {
                      await searchTestResults({
                        workspace: savedSearchInfo.workspaceName,
                        project: savedSearchInfo.projectName,
                        testRunId: savedSearchInfo.testRunId,
                        searchType: "all",
                        judgmentMismatchOnly:
                          searchFilters.judgmentMismatchOnly,
                      });
                    }
                  }
                  return result;
                }}
              />
              <TagInfoButton
                workspaceId={(() => {
                  const workspaceIdFromFilters = searchFilters?.workspace;
                  const workspaceIdFromSaved = savedSearchInfo?.workspaceId;
                  const finalWorkspaceId = workspaceIdFromSaved || workspaceIdFromFilters || directWorkspaceId;
                  return finalWorkspaceId || undefined;
                })()}
                projectId={(() => {
                  const projectIdFromFilters = searchFilters?.project;
                  const projectIdFromSaved = savedSearchInfo?.projectId;
                  const finalProjectId = projectIdFromSaved || projectIdFromFilters || directProjectId;
                  return finalProjectId || undefined;
                })()}
                testResults={currentTestResults}
                apiClient={apiClient}
                filteredResults={filteredResults}
                allProjectData={allProjectRawData}
                onDataCached={async (data) => {
                  // APIから取得したデータをキャッシュに保存
                  setAllProjectRawData(data);
                  console.log('タグ情報確認ボタンからデータをキャッシュに保存:', data.length, '件');

                  // 全件をUI形式に変換してナビゲーション用に保存
                  try {
                    const { transformApiTestResults } = await import('./utils/dataTransform');
                    const allTransformed = transformApiTestResults(
                      data,
                      {}, // workspaceSlugMap
                      {}, // projectSlugMap
                      apiConfig.sstfBaseUrl,
                      template || undefined
                    );

                    const allUiResults = allTransformed.map((result: any) =>
                      convertApiTestResultToTestResult(
                        result,
                        template || undefined,
                        testStepStatusIds || undefined
                      )
                    );
                    setAllProjectTestResultsUIFull(allUiResults);
                    console.log('UI形式のデータもキャッシュに保存:', allUiResults.length, '件');
                  } catch (error) {
                    console.error('データ変換エラー:', error);
                  }
                }}
              />
            </HeaderControls>
            <TestHeader
              testResult={selectedTest}
              stepJudgments={stepJudgments[selectedTestId]}
              stepInterpretations={stepInterpretations[selectedTestId] || {}}
              apiClient={apiClient}
              onJudgmentUpdate={async (testResultId, judgmentData) => {
                if (!selectedTest || !apiClient) return;
                
                // 選択されたAPIテスト結果を取得（現在の表示または全データから）
                const selectedApiResult = filteredResults.find(
                  (apiResult) => apiResult.scriptTestRunResult.result_id === selectedTest.id
                ) || allProjectTestResults.find(
                  (result: any) => result.scriptTestRunResult?.result_id === selectedTest.id
                );
                
                if (!selectedApiResult) {
                  console.error("対応するAPIテスト結果が見つかりません");
                  alert("テスト結果の更新に失敗しました");
                  return;
                }
                
                try {
                  // TestHeaderから渡されたステータスIDを使用
                  const statusId = judgmentData.test_result_status_id || 
                                  projectStatusIds?.testResultStatus.NG ||
                                  "458cb677-0708-5af4-b926-bb7d398db6bb";
                  
                  // 既存のvalueを取得してマージ
                  let existingValue: any = {};
                  if (selectedApiResult.scriptTestRunResult.value) {
                    try {
                      existingValue = JSON.parse(selectedApiResult.scriptTestRunResult.value);
                    } catch (error) {
                      console.warn("既存のvalueのパースに失敗:", error);
                    }
                  }
                  
                  // 既存のvalueと新しいタグ分析データをマージ
                  // tag_analysisは特別に深いマージが必要
                  const mergedValue = {
                    ...existingValue,
                    ...judgmentData,
                    tag_analysis: {
                      ...(existingValue.tag_analysis || {}),
                      ...judgmentData.tag_analysis
                    }
                  };
                  
                  // updateTestResultを使用して更新（元の処理と同じ）
                  await updateTestResult(
                    selectedApiResult.testCaseDetail.workspace_id,
                    selectedApiResult.testCaseDetail.project_id,
                    selectedApiResult.testRunId,
                    selectedApiResult.scriptTestRunResult.result_id,
                    selectedApiResult.scriptTestRunResult.script_test_run_matrix_id,
                    statusId,
                    undefined, // actualTimeInSeconds
                    JSON.stringify(mergedValue) // マージされたvalueをJSON文字列として渡す
                  );
                  
                  // 判定データから人間の判定文字列を抽出して状態を更新
                  const humanJudgmentText = judgmentData.tag_analysis?.human_judgment || null;
                  setHumanJudgment(humanJudgmentText);
                  console.log("判定が正常に保存されました");
                  
                  // 現在のテストを完了済みとしてマーク
                  setCompletedTests((prev) => new Set(prev).add(selectedTest.id));
                  
                } catch (error) {
                  console.error("判定の保存に失敗しました:", error);
                  alert("判定の保存に失敗しました");
                }
              }}
              webBaseUrl={apiConfig.webBaseUrl}
              apiTestResult={
                filteredResults.find(
                  (result) =>
                    result.scriptTestRunResult.result_id === selectedTestId
                ) || null
              }
              template={template}
              projectStatusIds={projectStatusIds?.testResultStatus}
              operationLog={operationLog}
              evidenceFiles={evidenceFiles}
              regressionBaseline={regressionBaseline}
            />
            <EvidenceViewer
              testResult={selectedTest}
              fileMetadata={selectedResultFiles}
              loadingFiles={loadingFiles}
              operationLog={operationLog}
              evidenceFiles={evidenceFiles}
              loadingOperationLog={loadingOperationLog}
              loadingEvidenceFiles={loadingEvidenceFiles}
              humanJudgment={humanJudgment}
              apiTestResult={
                filteredResults.find(
                  (apiResult) => apiResult.scriptTestRunResult.result_id === selectedTest.id
                )
              }
              projectStatusIds={projectStatusIds?.testResultStatus}
              template={template}
              stepJudgments={stepJudgments[selectedTestId]}
              onStepJudgmentChange={handleStepJudgmentChange}
              stepInterpretations={stepInterpretations[selectedTestId]}
              onStepInterpretationUpdate={handleStepInterpretationUpdate}
              initialStepIndex={selectedStepIndex}
              testTargets={testTargets}
              baselineUrls={baselineUrls}
              regressionBaseline={regressionBaseline}
              regressionThresholds={regressionThresholds}
              onInterpretationUpdate={async (testResultId, interpretationCorrect) => {
                try {
                  // 現在のvalueを取得して、ai_interpretation_correctのみ更新
                  const currentValue = selectedTest.value || {};
                  const updatedValue = {
                    ...currentValue,
                    tag_analysis: {
                      ...(currentValue.tag_analysis || {}),
                      ai_interpretation_correct: interpretationCorrect
                    }
                  };
                  await updateTestResultValue(testResultId, updatedValue);
                  console.log("AI解釈の正誤が保存されました");
                } catch (error) {
                  console.error("AI解釈の正誤の保存に失敗しました:", error);
                  throw error;
                }
              }}
            />
          </>
        )}
      </MainContent>

      <ConfigPanel isVisible={showConfigPanel}>
        <ConfigTitle>検索条件</ConfigTitle>

        <ConfigSection>
          {/* API設定は環境変数から自動設定 - 非表示 */}
          {/* API configuration is handled via environment variables */}

          <ConfigLabel>ワークスペース</ConfigLabel>
          <ConfigSelect
            value={searchFilters.workspace}
            onChange={(e) =>
              handleSearchFilterChange("workspace", e.target.value)
            }
            disabled={loading}
          >
            <option value="">ワークスペースを選択</option>
            {workspaces.map((workspace) => (
              <option
                key={workspace.workspace_id}
                value={workspace.workspace_id}
              >
                {workspace.name}
              </option>
            ))}
          </ConfigSelect>

          <ConfigLabel>プロジェクト</ConfigLabel>
          <ConfigSelect
            value={searchFilters.project}
            onChange={(e) =>
              handleSearchFilterChange("project", e.target.value)
            }
            disabled={loading || !searchFilters.workspace}
          >
            <option value="">プロジェクトを選択</option>
            {projects.map((project) => (
              <option key={project.project_id} value={project.project_id}>
                {project.name}
              </option>
            ))}
          </ConfigSelect>

          <ConfigLabel>テストラン</ConfigLabel>
          <ConfigSelect
            value={searchFilters.testRunId}
            onChange={(e) =>
              handleSearchFilterChange("testRunId", e.target.value)
            }
            disabled={loading || !searchFilters.project}
          >
            <option value="">（指定なし）</option>
            {testRuns.map((testRun) => (
              <option key={testRun.test_run_id} value={testRun.test_run_id}>
                {testRun.name}
              </option>
            ))}
          </ConfigSelect>

          <ConfigLabel>検索タイプ</ConfigLabel>
          <ConfigSelect
            value={searchFilters.searchType}
            onChange={(e) =>
              handleSearchFilterChange("searchType", e.target.value)
            }
          >
            <option value="analyzer_waiting">AIアナライズ待ち</option>
            <option value="needs_review">要確認のみ</option>
            <option value="all">全て</option>
          </ConfigSelect>

          <ConfigCheckboxRow>
            <input
              type="checkbox"
              checked={searchFilters.judgmentMismatchOnly}
              onChange={(e) => {
                const checked = e.target.checked;
                setSearchFilters((prev) => ({
                  ...prev,
                  judgmentMismatchOnly: checked,
                }));
                applyFilters({ judgmentMismatchOnly: checked });
              }}
            />
            <span>人間の判定とAIの判定が異なる結果のみ表示</span>
          </ConfigCheckboxRow>

          {(savedSearchInfo.workspaceName || savedSearchInfo.projectName) && (
            <SearchInfo>
              現在の検索条件:
              <br />
              {savedSearchInfo.workspaceName &&
                `ワークスペース: ${savedSearchInfo.workspaceName}`}
              <br />
              {savedSearchInfo.projectName &&
                `プロジェクト: ${savedSearchInfo.projectName}`}
              <br />
              {savedSearchInfo.testRunName &&
                `テストラン: ${savedSearchInfo.testRunName}`}
            </SearchInfo>
          )}

          <ConfigButtonGroup>
            <ConfigButton
              variant="primary"
              onClick={handleSearch}
              disabled={
                loading || !searchFilters.workspace || !searchFilters.project
              }
            >
              {loading ? "検索中..." : "検索実行"}
            </ConfigButton>
            {error && (
              <ConfigButton variant="secondary" onClick={clearError}>
                エラーをクリア
              </ConfigButton>
            )}
          </ConfigButtonGroup>
        </ConfigSection>

        <ConfigButtonGroup>
          <ConfigButton
            variant="secondary"
            onClick={() => setShowConfigPanel(false)}
          >
            閉じる
          </ConfigButton>
        </ConfigButtonGroup>

        {/* デバッグパネル */}
        <DebugPanel apiClient={apiClient} />
      </ConfigPanel>

      <CompletionOverlay isVisible={showCompletion}>
        <CompletionPanel>
          <CompletionTitle>
            {searchStatus === "preparing"
              ? "🔍 検索準備中"
              : searchStatus === "searching"
              ? "🔍 検索実行中"
              : error
              ? "❌ エラー"
              : completionStats.total === 0
              ? "📋 検索結果"
              : "🎉 テスト確認完了"}
          </CompletionTitle>
          <CompletionMessage>
            {searchStatus === "preparing"
              ? "保存された検索条件でテストケースを検索します..."
              : searchStatus === "searching"
              ? "テストケースを検索しています。しばらくお待ちください..."
              : error
              ? (() => {
                  // 認証エラーの判定
                  if (error.includes("401") || error.includes("403") || error.includes("Unauthorized") || error.includes("認証")) {
                    return "認証エラーが発生しました。設定画面でUser Tokenを確認してください。";
                  } else if (error.includes("404") || error.includes("Not Found")) {
                    return "APIエンドポイントが見つかりません。設定画面でURLを確認してください。";
                  } else if (error.includes("fetch") || error.includes("ネットワーク")) {
                    return "ネットワークエラーが発生しました。接続を確認してください。";
                  } else {
                    return `エラーが発生しました: ${error}`;
                  }
                })()
              : completionStats.total === 0
              ? "対象となるテストケースが見つかりませんでした。新しいテストケースがないか確認しています..."
              : "全てのテストケースの確認が完了しました。新しいテストケースがないか確認しています..."}
          </CompletionMessage>

          {searchStatus === "completed" && completionStats.total > 0 && (
            <CompletionStats>
              <CompletionStat>
                <CompletionStatLabel>確認済みテスト</CompletionStatLabel>
                <CompletionStatValue>
                  {completionStats.completed} / {completionStats.total}
                </CompletionStatValue>
              </CompletionStat>
              <CompletionStat>
                <CompletionStatLabel>成功</CompletionStatLabel>
                <CompletionStatValue style={{ color: "#4caf50" }}>
                  {completionStats.successCount}
                </CompletionStatValue>
              </CompletionStat>
              <CompletionStat>
                <CompletionStatLabel>失敗</CompletionStatLabel>
                <CompletionStatValue style={{ color: "#f44336" }}>
                  {completionStats.failedCount}
                </CompletionStatValue>
              </CompletionStat>
            </CompletionStats>
          )}

          {(searchStatus === "preparing" || searchStatus === "searching") && (
            <SearchingIndicator>
              <Spinner />
              {searchStatus === "preparing"
                ? "検索を準備中..."
                : "検索実行中..."}
            </SearchingIndicator>
          )}

          {isSearchingNewCases && searchStatus === "completed" && (
            <SearchingIndicator>
              <Spinner />
              新しいテストケースを検索中...
            </SearchingIndicator>
          )}

          {!isSearchingNewCases &&
            nextSearchCountdown > 0 &&
            searchStatus === "completed" && (
              <AutoSearchTimer>
                📅 次回自動検索まで: {formatTime(nextSearchCountdown)}
                <br />
                <small>新しいテストケースがないか定期的に確認します</small>
              </AutoSearchTimer>
            )}

          <CompletionButtonGroup>
            {searchStatus === "completed" && !error && (
              <CompletionButton
                onClick={handleManualSearch}
                disabled={isSearchingNewCases}
              >
                🔍 今すぐ検索
              </CompletionButton>
            )}
            {error && (
              <CompletionButton
                onClick={() => {
                  setShowCompletion(false);
                  setShowConfigPanel(true);
                  clearError(); // エラーをクリア
                }}
              >
                ⚙️ 設定を開く
              </CompletionButton>
            )}
            <CompletionButton
              variant="secondary"
              onClick={handleCompletionClose}
              disabled={
                searchStatus === "preparing" || searchStatus === "searching"
              }
            >
              ✖️ 閉じる
            </CompletionButton>
          </CompletionButtonGroup>
        </CompletionPanel>
      </CompletionOverlay>
    </AppContainer>
  );
};

// Main App component with AuthProvider
const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
